<template lang="html"> 
  <div class="intermediary">
    <!-- Breadcrumb and button container -->
    <div class="d-sm-flex align-items-center justify-content-between mg-b-20 mg-lg-b-25 mg-xl-b-30">
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb breadcrumb-style1 mg-b-10">
          <li class="breadcrumb-item">
            <router-link :to="{name: 'Dashboard'}">Admin</router-link>
          </li>
          <li class="breadcrumb-item active" aria-current="page">Intermediary View</li> 
        </ol>
      </nav>

      <!-- Verify Button aligned to the right (Conditional rendering based on status and kyc_status) -->
      <div class="ml-auto">
        <button 
          v-if="!isActiveAndKycActive" 
          class="btn btn-primary" 
          @click="openModal">
            Verify
        </button>
      </div>
    </div>

    <!-- Dark Overlay for Modal -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal"></div>

    <!-- Verify Intermediary Modal -->
    <div v-if="showModal" class="modal show">
      <div class="modal-content">
        <h3>Verify Intermediary</h3>
        <p>Are you sure you want to verify this intermediary?</p>

        <!-- Remarks Field (Always visible but required only for Reject) -->
        <div>
          <label for="remarks">Remarks</label>
          <textarea v-model="remarks" id="remarks" :required="isReject"></textarea>
          <!-- Prompt user to fill remarks -->
          <p v-if="isReject && !remarks" style="color: red; font-size: 12px;">Please provide remarks for rejecting the intermediary.</p>
        </div>

        <div class="modal-footer">
          <!-- Accept Button (No remarks required) -->
          <button @click="handleAction('accept')" class="btn btn-success">Accept</button>

          <!-- Reject Button (Remarks required) -->
          <button 
            @click="handleAction('reject')" 
            class="btn btn-danger" 
            :disabled="!remarks" 
            :title="!remarks ? 'Enter Remarks' : ''">Reject</button>

          <!-- Close Modal Button -->
          <button @click="closeModal" class="btn btn-secondary">Close</button>
        </div>
      </div>
    </div>

    <!-- Popup for success message -->
    <div v-if="showPopup" class="popup-overlay">
      <div class="popup-content">
        <h4>{{ popupMessage }}</h4>
        <button @click="closePopup" class="btn btn-primary">Close</button>
      </div>
    </div>

    <!-- Card displaying intermediary details (unchanged) -->
    <div class="row">
      <div class="col-sm">
        <div class="card mg-b-20 mg-lg-b-25">
          <div class="card-header pd-y-15 pd-x-20 d-flex align-items-center justify-content-between">
            <h6 class="tx-uppercase tx-semibold mg-b-0">Names: {{ intermediary.kyc && intermediary.kyc.length > 0 ? intermediary.kyc[0].intermediary_names : 'No Name available' }}</h6>
          </div><!-- card-header -->
          
          <div class="card-body pd-20 pd-lg-25">

            <div class="media align-items-center mg-b-20">
              <div class="avatar avatar-online">
                <img src="https://via.placeholder.com/500" class="rounded-circle" alt="">
              </div>
              <div class="media-body pd-l-15">
                <h6 class="mg-b-3">IRA Code: {{ intermediary.ira_code }}</h6>
                <span class="d-block tx-13 tx-color-03">Category: {{ intermediary.category }}</span>
                <span class="d-block tx-13 tx-color-03">Status: {{ intermediary.status }}</span>
                <span class="d-block tx-13 tx-color-03">KYC Status: {{ intermediary.kyc_status }}</span>
                <span class="d-block tx-13 tx-color-03">Description: {{ intermediary.kyc && intermediary.kyc.length > 0 ? intermediary.kyc[0].description : 'No description available' }}</span>
                <span class="d-none d-sm-block tx-12 tx-color-03 align-self-start">Profile Status: {{ intermediary.profile_status }}</span>
              </div>
              <span class="d-none d-sm-block tx-12 tx-color-03 align-self-start">{{ intermediary.created_date }}</span>
            </div>

            <div class="media align-items-center mg-b-20">
              <div class="media-body pd-l-15">
                <span class="d-block tx-13 tx-color-03">Address: {{ intermediary.kyc && intermediary.kyc.length > 0 ? intermediary.kyc[0].address : 'No address available' }}</span>
                <span class="d-block tx-13 tx-color-03">Contacts: {{ intermediary.kyc && intermediary.kyc.length > 0 ? intermediary.kyc[0].contacts : 'No contact available' }}</span>
                <span class="d-block tx-13 tx-color-03">Emails: {{ intermediary.kyc && intermediary.kyc.length > 0 ? intermediary.kyc[0].emails : 'No email available' }}</span>
                <span class="d-block tx-13 tx-color-03">KRA Pin: {{ intermediary.kyc && intermediary.kyc.length > 0 ? intermediary.kyc[0].kra_pin : 'No KRA Pin available' }}</span>
                <span class="d-block tx-13 tx-color-03">Profession: {{ intermediary.profession }}</span>
              </div>
            </div>

            <!-- Document Links to trigger popup for Preview -->
            <div class="documents">
              <h6>Documents:</h6>
              <ul>
                <li>
                  <a href="javascript:void(0);" @click="console.log('Document link clicked'); openDocumentPreview(intermediary.kyc[0].cr12_certificate_file_path)">CR12 Certificate</a>
                </li>
                <li>
                  <a href="javascript:void(0);" @click="console.log('Document link clicked'); openDocumentPreview(intermediary.kyc[0].incorporation_certificate_file_path)">Incorporation Certificate</a>
                </li>
                <li>
                  <a href="javascript:void(0);" @click="console.log('Document link clicked'); openDocumentPreview(intermediary.kyc[0].kra_certificate_file_path)">KRA Certificate</a>
                </li>
                <li>
                  <a href="javascript:void(0);" @click="console.log('Document link clicked'); openDocumentPreview(intermediary.kyc[0].memo_of_association_file_path)">Memo of Association</a>
                </li>
              </ul>
            </div>

            <!-- Display document preview in modal -->
            <div v-if="showDocumentModal" class="modal-overlay" @click="closeDocumentPreview">
              <div class="modal-content">
                <button class="btn-close" @click="closeDocumentPreview">X</button>
                <iframe v-if="documentPreviewUrl" :src="documentPreviewUrl" width="100%" height="600px"></iframe>
                <div v-else>No document selected or failed to load.</div>
              </div>
            </div>


          </div>

          <div class="card-footer bg-transparent pd-y-10 pd-sm-y-15 pd-x-10 pd-sm-x-20">
            <nav class="nav nav-with-icon tx-13">
              <a href="#" class="nav-link"><i data-feather="edit"></i> Edit</a>
            </nav>
          </div><!-- card-footer -->
        </div><!-- card -->
      </div>
    </div>

    <!-- Directors Section -->
    <template v-if="hasdirectors">
      <h1 class="directors-title">Directors</h1>
      <div class="directors-grid">
        <div v-for="(director, index) in intermediary.directors" :key="index" class="director-card">
          <div class="director-card-header">
            <h5 class="director-name">{{ director.title }} {{ director.names }}</h5>
            <p class="director-type">{{ director.type }}</p>
          </div>
          <div class="director-card-body">
            <p class="director-email">Email: <a :href="'mailto:' + director.email">{{ director.email }}</a></p>
            <p class="director-contact">Contact: {{ director.contact }}</p>
          </div>
        </div>
      </div>
    </template>

  </div>
</template>

<!-- <script>
export default {
	async created() {   

    },
    	data() {
			return {
			   intermediary:{},
         hasdirectors:false
            }
	},
    methods: {
           fetchIntermediary () {
                return new Promise(async (resolve, reject) => {
                    try {
                     let res = await req.callServerSecure({
                            resource: "intermediary",
                            action: "get",
                            data: {
                                intermediary_id: this.$route.params.id
                            },
                            meta_data: {}
                        });
                       this.intermediary = res.data.result.intermediary_details;
                       if( this.intermediary.length > 0) {
                         this.hasdirectors=true
                       }
                       console.log(this.intermediary.intermediary_details)

                       resolve()
                    }
                    catch(err) {
                        reject(err)
                    }
                })
           }  
     },
    async mounted() {
        await this.fetchIntermediary()
		Event.$emit("loader:show", false);
	}

     };
</script> -->

<script>
import { toRaw } from 'vue';  // Import Vue's `toRaw` function
import axios from 'axios';

export default {
  data() {
    return {
      intermediary: {},
      hasdirectors: false,
      showModal: false,  // Controls modal visibility for verification
      isReject: false,   // Whether the user selected "Reject"
      remarks: "",       // Stores remarks input
      intermediaryId: null,  // Dynamically set the intermediary ID
      showPopup: false, // Controls visibility of the popup
      popupMessage: "",
      
      // Added for document preview functionality
      showDocumentModal: false,
      documentPreviewUrl: '', // URL for the document to preview
    };
  },
  
  computed: {
    isActiveAndKycActive() {
      return this.intermediary.status === 'ACTIVE' && this.intermediary.kyc_status === 'ACTIVE';
    }
  },

  methods: {
    async fetchIntermediary() {
      try {
        let res = await req.callServerSecure({
          resource: "intermediary",
          action: "get",
          data: { intermediary_id: this.$route.params.id },
          meta_data: {}
        });

        console.log("Fetched API Response:", res);

        // Check if the API returned valid data
        if (!res.data || !res.data.result || !res.data.result.intermediary_details || !res.data.result.intermediary_details.intermediary_details) {
          console.error("No intermediary details found in API response.");
          return;
        }

        // Access the actual intermediary details
        this.intermediary = res.data.result.intermediary_details.intermediary_details; 
        console.log("Fetched intermediary details:", this.intermediary);

        const rawIntermediary = toRaw(this.intermediary);
        console.log("Raw intermediary details:", rawIntermediary);

        // Log intermediary ID values
        console.log("Intermediary ID (raw):", rawIntermediary.intermediary_id);

        // Assign intermediaryId correctly from `intermediary_id`
        this.intermediaryId = rawIntermediary.intermediary_id;
        console.log("Final Intermediary ID after assignment:", this.intermediaryId);

        // Check if directors exist
        if (Array.isArray(this.intermediary.directors) && this.intermediary.directors.length > 0) {
          this.hasdirectors = true;
        }
      } catch (err) {
        console.error("Error fetching intermediary:", err);
      }
    },

    // Handle Accept/Reject API call
    async handleAction(action) {
      console.log("Intermediary ID before API call:", this.intermediaryId);

      // If trying to reject and remarks are empty, we still allow the action but send the empty remarks.
      const apiUrl = "https://api.prod.nexus.ke/v1/api/web/v1/admin"; // Replace with your actual API URL

      // Prepare the payload
      const payload = {
        action: "intermediary_verification",
        data: {
          intermediary_id: this.intermediaryId, // Dynamically fetched intermediary ID
          admin_decision: action === "accept" ? "approved" : "rejected", // Set decision based on action (approved or rejected)
          verification_remarks: this.remarks || "", // Always send remarks, even if blank
        },
        resource: "intermediary",
      };

      console.log("Payload to send:", payload);

      try {
        const response = await axios.post(apiUrl, payload, {
          headers: {
            "Content-Type": "application/json", // Ensure you're sending JSON data
          },
        });

        if (response.status === 200) {
          console.log("API Response:", response.data);
          this.closeModal(); // Close the modal after successful API call
          this.showPopup = true; // Show the success popup
          this.popupMessage = action === "accept" ? "Intermediary Accepted Successfully!" : "Intermediary Rejected Successfully!";
        }
      } catch (error) {
        console.error("Error making API request:", error);
      }
    },

    // Close the popup
    closePopup() {
      this.showPopup = false;
      this.popupMessage = ""; // Reset message
    },

    // Close the modal
    closeModal() {
      this.showModal = false;
      this.remarks = ""; // Reset remarks field
      this.isReject = false; // Reset rejection flag
    },

    // Open the modal
    openModal() {
      this.showModal = true;
    },

    // Open document preview modal
    openDocumentPreview(url) {
      console.log("openDocumentPreview called with URL:", url);
      if (url) {
        const baseUrl = "https://api.prod.nexus.ke/";
        let fullUrl = url;

        // Only prepend baseUrl if the url doesn't already start with "http" or "https"
        if (!url.startsWith("http://") && !url.startsWith("https://")) {
          fullUrl = baseUrl + url;  // Prepend the base URL if it's not a full URL
        }

        console.log("Full document URL:", fullUrl);

        this.documentPreviewUrl = fullUrl;  // Set the URL for the iframe
        this.showDocumentModal = true;   // Show the modal
      } else {
        console.error("No URL provided for document preview.");
      }
    },

    // Close document preview modal
    closeDocumentPreview() {
      this.showDocumentModal = false;
      this.documentPreviewUrl = ''; // Clear the URL
    }
  },

  async mounted() {
    await this.fetchIntermediary();
    Event.$emit("loader:show", false);
  }
};
</script>

<style lang="less">
/* Dark overlay covering the entire screen (existing modal and new document preview modal) */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7); /* Dark background */
  z-index: 1050; /* Overlay should be on top of other elements but below modal */
  pointer-events: none; /* Allow clicks to pass through */
}

/* Modal content */
.modal-content {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);  /* Center the modal */
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  z-index: 1060;  /* Modal is on top of the overlay */
  width: 80%;
  max-height: 80vh;  /* Add a max height */
  overflow-y: auto;  /* Allow scrolling if content overflows */
}

/* Styles for iframe within the modal (Document Preview) */
.modal-content iframe {
  width: 100%;
  height: 500px; /* Adjust as needed */
  border: none;
}

/* Close button for Document Preview modal */
.modal-content .btn-close {
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
}

/* Ensure that the modal content is centered and responsive for both modals (existing Verify modal and new Document Preview modal) */
.modal.show {
  display: block;  /* Will be shown when 'showModal' or 'showDocumentModal' is true */
}

/* Additional styling to manage the popup overlay and content for Document Preview */
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
}

.popup-content {
  background-color: #fff;
  padding: 20px;
  border-radius: 8px;
  max-width: 400px;
  text-align: center;
}

.popup-content h4 {
  margin-bottom: 20px;
  font-size: 18px;
}

.popup-content button {
  background-color: #007bff;
  color: white;
  padding: 10px 20px;
  border-radius: 5px;
}

/* Existing Modal Styling for Verify Intermediary Modal */
.modal {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);  /* Center the modal */
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  z-index: 1060;  /* Modal is on top of the overlay */
  width: 400px;  /* Adjust width if necessary */
  max-height: 80vh;  /* Add a max height */
  overflow-y: auto;  /* Allow scrolling if content overflows */
  display: none;  /* Default to hidden, will be shown via Vue */
}

/* Modal footer buttons */
.modal-footer {
  margin-top: 15px;
  display: flex;
  justify-content: space-between;
  gap: 10px; /* Add spacing between buttons */
}

/* Textarea for remarks */
textarea {
  width: 100%;
  height: 100px;
  margin-top: 8px;
  resize: vertical; /* Allow resizing only vertically */
}

/* Modal footer buttons */
.modal-footer {
  margin-top: 15px;
  display: flex;
  justify-content: space-between;
  gap: 10px; /* Add spacing between buttons */
}

/* Primary button styling */
.btn-primary {
  background-color: #007bff; /* Adjust color if needed */
  color: white;
  border-radius: 5px;
  padding: 10px 20px;
  font-size: 16px;
}

.btn-primary:hover {
  background-color: #0056b3; /* Add hover effect */
}

/* Existing styles for intermediary details, directors, and other sections */
.directors-title {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 20px;
  color: #2c3e50;
}

.directors-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.director-card {
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.director-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.2);
}

.director-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.director-name {
  font-size: 18px;
  font-weight: bold;
  color: #34495e;
  margin: 0;
}

.director-type {
  font-size: 14px;
  font-style: italic;
  color: #7f8c8d;
}

.director-card-body {
  margin-top: 15px;
}

.director-email,
.director-contact {
  font-size: 14px;
  color: #2c3e50;
}

.director-email a {
  color: #3498db;
  text-decoration: none;
}

.director-email a:hover {
  text-decoration: underline;
}
</style>